import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

// utils
import { SCHEMA_TYPE_ENUM, SCHEMA_TYPE_MULTI_ENUM, SCHEMA_TYPE_LONG } from '../../util/types';
import { convertCategoriesToSelectTreeOptions, constructQueryParamName } from '../../util/search';
import { getEffectiveLocale } from '../../util/genericHelpers';

// ✅ Thai label maps
import { FIELD_HEADING_TH, OPTION_LABEL_TH } from '../../util/thaiFields';
import { sortOptionsByLabel } from './SearchPage.shared';

// component imports
import SelectSingleFilter from './SelectSingleFilter/SelectSingleFilter';
import SelectMultipleFilter from './SelectMultipleFilter/SelectMultipleFilter';
import BookingDateRangeFilter from './BookingDateRangeFilter/BookingDateRangeFilter';
import KeywordFilter from './KeywordFilter/KeywordFilter';
import PriceFilter from './PriceFilter/PriceFilter';
import IntegerRangeFilter from './IntegerRangeFilter/IntegerRangeFilter';
import SeatsFilter from './SeatsFilter/SeatsFilter';

const isThaiLocale = l => {
  const loc = String(l || '').toLowerCase();
  return loc === 'th' || loc.startsWith('th-');
};

// ✅ localize enum option labels
const localizeEnumOptions = (enumOptions = [], selectedLanguage, optionMap = {}) => {
  if (!isThaiLocale(selectedLanguage) || !Array.isArray(enumOptions)) return enumOptions;

  return enumOptions.map(o => ({
    ...o,
    label: optionMap?.[o.option] || o.label,
  }));
};

// ✅ localize filter label (field heading) by key
const localizeFilterLabel = (label, key, selectedLanguage) => {
  if (!isThaiLocale(selectedLanguage)) return label;
  return FIELD_HEADING_TH?.[key] || label;
};

// ✅ localize category tree (this is what your Category filter needs)
const localizeCategories = (categories = [], selectedLanguage, categoryMap = {}) => {
  if (!isThaiLocale(selectedLanguage) || !Array.isArray(categories)) return categories;

  const localizeOne = c => {
    if (!c) return c;

    // Try best possible identifier (Sharetribe configs vary)
    const id =
      (typeof c.id === 'string' ? c.id : null) ||
      (typeof c.key === 'string' ? c.key : null) ||
      (typeof c.categoryId === 'string' ? c.categoryId : null);

    // Some configs use "name", some use "label"
    const currentName = c.name || c.label;

    const translatedName =
      (id && categoryMap?.[id]) || (currentName && categoryMap?.[currentName]) || currentName;

    // Children may be "subcategories" or "categories"
    const childrenKey = Array.isArray(c.subcategories)
      ? 'subcategories'
      : Array.isArray(c.categories)
      ? 'categories'
      : null;

    const next = { ...c };
    if ('name' in next) next.name = translatedName;
    if ('label' in next) next.label = translatedName;

    if (childrenKey) {
      next[childrenKey] = next[childrenKey].map(localizeOne);
    }

    return next;
  };

  return categories.map(localizeOne);
};

const FilterComponent = props => {
  const {
    idPrefix,
    config,
    urlQueryParams,
    initialValues,
    getHandleChangedValueFn,
    listingCategories,
    marketplaceCurrency,
    intl,
    ...rest
  } = props;

  // ✅ your requested flow
  const { currentUser } = useSelector(state => state.user);
  const selectedLanguage = getEffectiveLocale(currentUser);

  const { key, schemaType } = config;
  const { liveEdit, showAsPopup, sortOptions = false } = rest;

  const useHistoryPush = liveEdit || showAsPopup;
  const prefix = idPrefix || 'SearchPage';
  const componentId = `${prefix}.${key.toLowerCase()}`;
  const name = key.replace(/\s+/g, '-');

  // Default filters
  switch (schemaType) {
    case 'category': {
      const { scope, isNestedEnum, nestedParams } = config;
      const queryParamNames = nestedParams?.map(p => constructQueryParamName(p, scope));

      // ✅ FIX: localize categories BEFORE converting to select-tree options
      const localizedCategories = useMemo(
        () => localizeCategories(listingCategories || [], selectedLanguage, OPTION_LABEL_TH),
        [listingCategories, selectedLanguage]
      );

      return (
        <SelectSingleFilter
          id={componentId}
          name={key}
          label={intl.formatMessage({ id: 'FilterComponent.categoryLabel' })}
          queryParamNames={queryParamNames}
          initialValues={initialValues(queryParamNames, liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          options={convertCategoriesToSelectTreeOptions(localizedCategories)}
          isNestedEnum={isNestedEnum}
          {...rest}
        />
      );
    }

    case 'price': {
      const { min, max, step } = config;
      return (
        <PriceFilter
          id={componentId}
          label={intl.formatMessage({ id: 'FilterComponent.priceLabel' })}
          queryParamNames={[key]}
          initialValues={initialValues([key], liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          min={min}
          max={max}
          step={step}
          marketplaceCurrency={marketplaceCurrency}
          {...rest}
        />
      );
    }

    case 'keywords':
      return (
        <KeywordFilter
          id={componentId}
          label={intl.formatMessage({ id: 'FilterComponent.keywordsLabel' })}
          name={name}
          queryParamNames={[key]}
          initialValues={initialValues([key], liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          {...rest}
        />
      );

    case 'dates': {
      const { dateRangeMode } = config;
      const isNightlyMode = dateRangeMode === 'night';
      return (
        <BookingDateRangeFilter
          id={componentId}
          label={intl.formatMessage({ id: 'FilterComponent.datesLabel' })}
          queryParamNames={[key]}
          initialValues={initialValues([key], liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          minimumNights={isNightlyMode ? 1 : 0}
          {...rest}
        />
      );
    }

    case 'seats': {
      return (
        <SeatsFilter
          id={componentId}
          name={name}
          label={intl.formatMessage({ id: 'FilterComponent.seatsLabel' })}
          queryParamNames={[key]}
          initialValues={initialValues([key], liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          {...rest}
        />
      );
    }

    default:
      break;
  }

  // Custom extended data filters
  switch (schemaType) {
    case SCHEMA_TYPE_ENUM: {
      const { scope, enumOptions, filterConfig = {} } = config;
      const { label, filterType } = filterConfig;
      const queryParamNames = [constructQueryParamName(key, scope)];

      const localizedLabel = localizeFilterLabel(label, key, selectedLanguage);
      const localizedEnumOptions = useMemo(() => {
        const options = localizeEnumOptions(enumOptions || [], selectedLanguage, OPTION_LABEL_TH);
        return sortOptions ? sortOptionsByLabel(options, selectedLanguage) : options;
      }, [enumOptions, selectedLanguage, sortOptions]);

      return filterType === 'SelectSingleFilter' ? (
        <SelectSingleFilter
          id={componentId}
          label={localizedLabel}
          name={name}
          queryParamNames={queryParamNames}
          initialValues={initialValues(queryParamNames, liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          options={localizedEnumOptions}
          isNestedEnum={false}
          {...rest}
        />
      ) : (
        <SelectMultipleFilter
          id={componentId}
          label={localizedLabel}
          name={name}
          queryParamNames={queryParamNames}
          initialValues={initialValues(queryParamNames, liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          options={localizedEnumOptions}
          schemaType={schemaType}
          {...rest}
        />
      );
    }

    case SCHEMA_TYPE_MULTI_ENUM: {
      const { scope, enumOptions, filterConfig = {} } = config;
      const { label, searchMode } = filterConfig;
      const queryParamNames = [constructQueryParamName(key, scope)];

      const localizedLabel = localizeFilterLabel(label, key, selectedLanguage);
      const localizedEnumOptions = useMemo(() => {
        const options = localizeEnumOptions(enumOptions || [], selectedLanguage, OPTION_LABEL_TH);
        return sortOptions ? sortOptionsByLabel(options, selectedLanguage) : options;
      }, [enumOptions, selectedLanguage, sortOptions]);

      return (
        <SelectMultipleFilter
          id={componentId}
          label={localizedLabel}
          name={name}
          queryParamNames={queryParamNames}
          initialValues={initialValues(queryParamNames, liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          options={localizedEnumOptions}
          schemaType={schemaType}
          searchMode={searchMode}
          {...rest}
        />
      );
    }

    case SCHEMA_TYPE_LONG: {
      const { minimum, maximum, scope, step, filterConfig = {} } = config;
      const { label } = filterConfig;
      const queryParamNames = [constructQueryParamName(key, scope)];

      const localizedLabel = localizeFilterLabel(label, key, selectedLanguage);

      return (
        <IntegerRangeFilter
          id={componentId}
          label={localizedLabel}
          name={name}
          queryParamNames={queryParamNames}
          initialValues={initialValues(queryParamNames, liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          min={minimum}
          max={maximum}
          step={step}
          {...rest}
        />
      );
    }

    default:
      return null;
  }
};

export default FilterComponent;
